import { useState } from "react";
import { useToast } from "@/components/feature/Toast";

export interface LocationValue {
  country: string;
  province: string;
  city: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
}

interface LocationPickerProps {
  value: LocationValue;
  onChange: (patch: Partial<LocationValue>) => void;
}

const DEFAULT_LAT = 18.7357;
const DEFAULT_LNG = -70.1627;

const inputClass =
  "w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-sm font-semibold text-foreground-800";

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
}

export default function LocationPicker({
  value,
  onChange,
}: LocationPickerProps) {
  const { showToast } = useToast();
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const hasCoords = value.latitude != null && value.longitude != null;
  const embedSrc = hasCoords
    ? `https://www.google.com/maps?q=${value.latitude},${value.longitude}&z=15&output=embed`
    : `https://www.google.com/maps?q=${DEFAULT_LAT},${DEFAULT_LNG}&z=13&output=embed`;

  function updateFromGeocode(lat: number, lng: number) {
    onChange({ latitude: lat, longitude: lng });
    setConfirmed(false);
  }

  // Geocodificación inversa: a partir de coordenadas rellena la dirección completa.
  async function reverseGeocode(lat: number, lng: number): Promise<Partial<LocationValue>> {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return {};
    const data = (await res.json()) as NominatimResult;
    const city =
      data.address?.city ?? data.address?.town ?? data.address?.village ?? "";
    const province = data.address?.state ?? "";
    const country = data.address?.country ?? "";
    return {
      address: data.display_name ?? "",
      city,
      province,
      country,
    };
  }

  async function handleSearch() {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        query.trim()
      )}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("No se pudo buscar");
      const data = (await res.json()) as NominatimResult[];
      if (!data.length) {
        showToast("No encontramos esa ubicación.", "error");
        return;
      }
      const r = data[0];
      const city = r.address?.city ?? r.address?.town ?? r.address?.village ?? "";
      const province = r.address?.state ?? "";
      const country = r.address?.country ?? "";
      onChange({
        latitude: Number(r.lat),
        longitude: Number(r.lon),
        city: city || value.city,
        province: province || value.province,
        country: country || value.country,
        address: r.display_name || value.address,
      });
      setConfirmed(false);
    } catch {
      showToast("No se pudo buscar la ubicación. Probá con otra dirección.", "error");
    } finally {
      setSearching(false);
    }
  }

  function handleLocate() {
    if (!navigator.geolocation) {
      showToast("Tu navegador no soporta geolocalización.", "error");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          const reverse = await reverseGeocode(lat, lng);
          onChange({
            latitude: lat,
            longitude: lng,
            city: reverse.city || value.city,
            province: reverse.province || value.province,
            country: reverse.country || value.country,
            address: reverse.address || value.address,
          });
        } catch {
          onChange({ latitude: lat, longitude: lng });
        }
        setConfirmed(false);
        setLocating(false);
      },
      () => {
        showToast("No pudimos obtener tu ubicación. Buscá tu dirección.", "error");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function handleConfirm() {
    if (!hasCoords) {
      showToast("Buscá tu dirección o usá tu ubicación actual.", "error");
      return;
    }
    setConfirmed(true);
    showToast("Ubicación confirmada.", "success");
  }

  return (
    <div className="space-y-4">
      {/* Búsqueda */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Buscar dirección, ciudad o negocio…"
            className={`${inputClass} pl-9`}
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching}
          className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          {searching ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-search-line"></i>
          )}
          Buscar
        </button>
        <button
          type="button"
          onClick={handleLocate}
          disabled={locating}
          className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
        >
          {locating ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-map-pin-user-line"></i>
          )}
          Mi ubicación
        </button>
      </div>

      {/* Mapa */}
      <div className="overflow-hidden rounded-lg border border-background-200">
        <iframe
          title="Mapa de ubicación"
          src={embedSrc}
          className="h-64 w-full"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>

      {/* Campos de dirección */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="loc-address">
            Dirección
          </label>
          <input
            id="loc-address"
            type="text"
            value={value.address}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder="Calle, número, referencia…"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="loc-city">
            Ciudad
          </label>
          <input
            id="loc-city"
            type="text"
            value={value.city}
            onChange={(e) => onChange({ city: e.target.value })}
            placeholder="Punta Cana"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="loc-province">
            Provincia
          </label>
          <input
            id="loc-province"
            type="text"
            value={value.province}
            onChange={(e) => onChange({ province: e.target.value })}
            placeholder="La Altagracia"
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="loc-country">
            País
          </label>
          <input
            id="loc-country"
            type="text"
            value={value.country}
            onChange={(e) => onChange({ country: e.target.value })}
            placeholder="República Dominicana"
            className={inputClass}
          />
        </div>
      </div>

      {/* Confirmar */}
      <div className="flex items-center justify-between rounded-md border border-background-200 bg-background-100/50 p-3">
        <div className="flex items-center gap-2 text-sm">
          {confirmed ? (
            <>
              <i className="ri-checkbox-circle-fill text-primary-600"></i>
              <span className="font-medium text-foreground-800">
                Ubicación confirmada
              </span>
            </>
          ) : (
            <>
              <i className="ri-map-pin-line text-foreground-400"></i>
              <span className="text-foreground-600">
                {hasCoords
                  ? "Confirmá que el punto sea correcto."
                  : "Aún no seleccionaste un punto."}
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={handleConfirm}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <i className="ri-check-line"></i>
          Confirmar ubicación
        </button>
      </div>

      {hasCoords && (
        <p className="text-xs text-foreground-400">
          Coordenadas (privadas): {value.latitude?.toFixed(5)},{" "}
          {value.longitude?.toFixed(5)}
        </p>
      )}
    </div>
  );
}